<script lang="ts" setup>
import type { IProps } from './wellbeing-sale--hero.types'

withDefaults(defineProps<IProps>(), {
  type: 'primary',
})
const { isMobile } = useBSBreakpoints()
</script>

<template>
  <section :class="['banner', `banner--${type}`]">
    <img
      v-if="isMobile"
      :src="mobileImage"
      :class="[
        'banner__cover',
        'banner__cover--mobile'
      ]"
    >

    <div class="banner__inner">
      <div :class="['banner__content', `banner__content--${type}`]">
        <div class="banner__header">
          <h1
            class="banner__title"
            v-html="header"
          />

          <h4
            :class="['banner__text', `banner__text--${type}`]"
            v-html="text"
          />
        </div>

        <UiButton
          v-if="button && button.text"
          variant="primary"
          :to="button.to"
        >
          {{ button.text }}

          <img
            class="banner__arrow"
            src="/icons/arrow_white.svg"
            alt=""
          >
        </UiButton>

        <img
          :src="desktopImage"
          class="banner__cover"
        >
      </div>

      <div class="banner__cloud">
        {{ cloudText }}
      </div>
    </div>

    <div class="banner__cloud banner__cloud--mobile">
      {{ cloudText }}
    </div>
  </section>
</template>

<style lang="scss" scoped>
.banner {
  position: relative;
  min-height: 100vh;
  padding: 8.8rem 2.8rem 0 2.8rem;
  overflow: hidden;
  border-radius: 0 0 2.4rem 2.4rem;

  &--secondary {
    min-height: 44.5rem;
  }

  &__cover {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 0;
    display: none;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: bottom;

    &--mobile {
      z-index: 1;
      display: flex;
    }
  }

  &__inner {
    position: relative;
    z-index: 2;
    max-width: 115.2rem;
    width: 100%;
    margin: 0 auto;
  }

  &__arrow {
    margin-left: 0.8rem;
  }

  &__content {
    display: flex;
    flex-direction: column;
    height: 100%;
    font-weight: 500;
  }

  &__header {
    margin-bottom: 2.4rem;

  }

  &__title {
    margin-bottom: 1.6rem;
    font-size: 4rem;
    line-height: 4.8rem;

    :deep() {
      span {
        color: var(--span-color);
      }
    }
  }

  &__text {
    font-size: 1.6rem;
    line-height: 2.4rem;

    :deep(.small) {
      display: block;
      margin-top: 1.4rem;
      font-size: 1.4rem;
      line-height: 2rem;
    }
  }

  &__info {
    font-size: 1.6rem;
    line-height: 2.4rem;
  }

  &__cloud {
    @include body-1;
    display: none;
    width: 34.2rem;
    margin-top: 5rem;
    padding: 1.6rem 2.4rem;
    background-color: #F4EFFE;
    border-radius: 1.6rem;

    &--mobile {
      position: absolute;
      bottom: 3rem;
      left: 50%;
      z-index: 2;
      display: flex;
      width: 28rem;
      transform: translate(-43%, 0);
    }
  }
}

@include atXS {
  .banner {
    min-height: 52vh;

    &__cover {
      &--mobile {
        z-index: -1;
      }
    }

    &__content {
      width: 60%;
    }

    &__title {
      font-size: 4.8rem;
      line-height: 5.6rem;
    }

    &__text {
      max-width: 75%;
      font-size: 1.8rem;

      :deep(.small) {
        margin-top: 1.6rem;
        font-size: 1.6rem;
        line-height: 2.4rem;
      }
    }
  }
}

@include atS {
  .banner {
    display: flex;
    align-items: center;
    min-height: 100vh;

    &--secondary {
      min-height: 51rem;
    }

    &__title {
      font-size: 5.6rem;
      line-height: 6.4rem;
    }

    &__text {
      margin-bottom: 3.2rem;
      font-size: 2rem;
      line-height: 2.8rem;

      :deep(.small) {
        margin-top: 2.4rem;
        font-size: 1.6rem;
      }
    }
  }
}

@include atM  {
  .banner {
    &__header {
      margin-bottom: 4.8rem;
    }

    &__title {
      margin-bottom: 4rem;
      font-size: 6.4rem;
      line-height: 7.2rem;
    }

    &__text {
      max-width: 80%;

      &--secondary {
        @include body-1-medium;
        max-width: 75%;
      }
    }

    &__cloud {
      position: absolute;
      right: 6rem;
      bottom: -5rem;
      display: flex;
      margin: 0;

      &--mobile {
        display: none;
      }
    }

    &__cover {
      position: absolute;
      top: unset;
      right: -3rem;
      bottom: -10rem;
      left: unset;
      z-index: 0;
      display: flex;
      max-width: unset;
      width: 65rem;
      max-height: unset;
      height: 51rem;
    }
  }
}

@include atXL {
  .banner {
    &__inner {
      max-width: 134rem;
    }

    &__content {
      width: 50%;

      &--secondary {
        width: 100%;
      }
    }

    &__title {
      margin-bottom: 3.2rem;
      font-size: 8rem;
      line-height: 8.8rem;
    }

    &__text {
      font-size: 2.4rem;
      line-height: 3.2rem;

      &--secondary {
        @include body-1-medium;
        max-width: 75%;
      }

      :deep(.small) {
        margin-top: 2.4rem;
        font-size: 1.8rem;
      }
    }
  }
}
</style>
